import { GeometryEntry } from "@org-avp/avp-avengers-geometry-service-api";
import { Brand } from "@org-avp/avp-avengers-user-service-api";

const defaultMasterFileTypes = [".csb", ".abc"];
const masterFileTypes = new Map<Brand, Set<string>>([
  [Brand.PORSCHE, new Set<string>([".vrut", ".abc"])],
]);

export const acceptedFileTypesList = [".csb", ".vrut", ".abc", ".fbx", ".usd", ".jt", ".catpart", ".step", ".stp", ".iges", ".igs"];
export const acceptedFileTypes = acceptedFileTypesList.join(",");

export const stringifyVersion = (geometry: GeometryEntry): string => {
  return padLeft(geometry.version.toString(), "0", 3);
};

export const stringifyAlternative = (geometry: GeometryEntry): string => {
  return geometry.alternative?.toString() ?? "---";
};

const GEOMETRY_KEY_REGEX = /^(S\.)?[0-9A-Z ]{3}\.[0-9A-Z]{3}\.[0-9A-Z]{3}(\.[0-9A-Z]{1,2})?$/;
export const extractGeometryKey = (fileName: string): string | undefined => {
  const [geometryKey] = fileName.split("_");
  if (geometryKey && geometryKey.match(GEOMETRY_KEY_REGEX)) {
    return geometryKey;
  }

  return undefined;
};

const GEOMETRY_HASH_REGEX = /^[0-9a-zA-Z]+$/;
export const extractGeometryHash = (fileName: string): string | undefined => {
  const [, geometryHash] = fileName.split(".").reverse();
  if (geometryHash && geometryHash.match(GEOMETRY_HASH_REGEX)) {
    return geometryHash;
  }

  return undefined;
};

const REPRESENTATION_HASH_REGEX = /^[0-9a-zA-Z]{2}$/;
export const extractRepresentationHash = (fileName: string): string | undefined => {
  const [, , representationHash] = fileName.split(".").reverse();
  if (representationHash && representationHash.match(REPRESENTATION_HASH_REGEX)) {
    return representationHash;
  }

  return undefined;
};

// schema: (<geoKey>_<pda>_<ver>_<alt>_)?<representationName>(.<reprHash>)?(.<geoHash>)?.<fileExt>
const REPRESENTATION_NAME_REGEX = /^((([-. 0-9a-zA-Z]+_){4})?(.+?))(\.[0-9a-zA-Z]{2})?(\.[0-9a-zA-Z]+)?(\.[^.]+)$/;
export const extractRepresentationName = (fileName: string): string | undefined => {
  const regexMatch = fileName.match(REPRESENTATION_NAME_REGEX);
  if (regexMatch != null) {
    // if the match does not contain a geometry key, take everything before the first dot
    // otherwise, take the 4th match group, which cuts off the geometry information before the representation name
    const hasGeometryKey = extractGeometryKey(fileName) != null;
    const matchGroupToUse = hasGeometryKey ? 4 : 1;
    return regexMatch[matchGroupToUse];
  }

  return undefined;
};

export const removeFileExtension = (fileName: string): string => {
  return fileName.substring(0, fileName.lastIndexOf("."));
};

export const getMasterFileTypes = (brand: Brand): string[] => {
  const fileTypes = masterFileTypes.get(brand);
  return fileTypes ? Array.from(fileTypes.values()) : defaultMasterFileTypes;
};

export const isTreatmentStateMasterFileName = (brand: Brand, fileName: string): boolean => {
  const fileExtension = `.${extractFileExtension(fileName.toLowerCase())}`;
  const fileTypes = masterFileTypes.get(brand);
  return fileTypes ? fileTypes.has(fileExtension) : defaultMasterFileTypes.includes(fileExtension);
};

export const extractFileExtension = (fileName: string): string => fileName.lastIndexOf(".") < 0 ? "" : fileName.substring(fileName.lastIndexOf(".") + 1);

const padLeft = (value: string, padChar: string, length: number): string => {
  let result = value;
  while (result.length < length) {
    result = padChar + result;
  }

  return result;
};
